/* ============================================================
   Archetipas theme — interactions
   - Reveal-on-scroll animations
   - Reviews carousel
   - Accordion (touch/click support; hover handled by CSS)
   ============================================================ */
(function () {
  'use strict';

  /* Shared scroll/resize — one passive listener each instead of many */
  var scrollFns = [];
  var resizeFns = [];
  var scrollQueued = false;
  var resizeQueued = false;

  function onScroll(fn) { scrollFns.push(fn); }
  function onResize(fn) { resizeFns.push(fn); }

  function bindWindowListeners() {
    if (!scrollFns.length && !resizeFns.length) return;
    if (scrollFns.length) {
      window.addEventListener('scroll', function () {
        if (scrollQueued) return;
        scrollQueued = true;
        requestAnimationFrame(function () {
          scrollQueued = false;
          for (var i = 0; i < scrollFns.length; i++) scrollFns[i]();
        });
      }, { passive: true });
    }
    if (resizeFns.length) {
      window.addEventListener('resize', function () {
        if (resizeQueued) return;
        resizeQueued = true;
        requestAnimationFrame(function () {
          resizeQueued = false;
          for (var i = 0; i < resizeFns.length; i++) resizeFns[i]();
        });
      }, { passive: true });
    }
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) { observer.observe(el); });
  }

  /* ---------- Partners marquee (seamless loop) ---------- */
  function initPartnersMarquee() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.querySelectorAll('[data-partners-marquee]').forEach(function (track) {
      if (track.dataset.marqueeInit === 'true') return;

      var wrap = track.closest('.partners--marquee');
      var groups = track.querySelectorAll('.partners__group');
      if (!wrap || groups.length < 2) return;

      var reverse = wrap.classList.contains('partners--reverse');
      var duration = parseFloat(wrap.style.getPropertyValue('--marquee-duration')) || 32;
      var paused = false;
      var pos = 0;
      var distance = 0;
      var rafId = 0;
      var lastTs = 0;

      wrap.addEventListener('mouseenter', function () {
        paused = true;
        track.classList.add('is-paused');
      });
      wrap.addEventListener('mouseleave', function () {
        paused = false;
        track.classList.remove('is-paused');
        lastTs = performance.now();
      });

      function measure() {
        var trackGap = parseFloat(getComputedStyle(track).gap) || 72;
        var groupWidth = groups[0].getBoundingClientRect().width;
        distance = groupWidth + trackGap;
        if (distance > 0) {
          track.style.setProperty('--marquee-distance', distance + 'px');
        }
        return distance > 1;
      }

      function tick(ts) {
        if (!lastTs) lastTs = ts;
        if (!paused && distance > 0) {
          var delta = (ts - lastTs) / 1000;
          var speed = distance / duration;
          pos += (reverse ? speed : -speed) * delta;

          if (pos <= -distance) pos += distance;
          if (pos >= distance) pos -= distance;

          track.style.transform = 'translate3d(' + pos + 'px,0,0)';
        }
        lastTs = ts;
        rafId = requestAnimationFrame(tick);
      }

      function start() {
        measure();
        track.classList.add('is-ready', 'is-animated');
        track.dataset.marqueeInit = 'true';
        lastTs = performance.now();
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(tick);
      }

      // Eager start: don't wait forever on lazy/cached images.
      // Remeasure as images finish so the loop stays seamless.
      var imgs = Array.prototype.slice.call(track.querySelectorAll('img'));
      var started = false;

      function tryStart() {
        if (started) {
          measure();
          return;
        }
        started = true;
        start();
      }

      imgs.forEach(function (img) {
        if (img.complete) return;
        img.addEventListener('load', function () {
          measure();
          tryStart();
        }, { once: true });
        img.addEventListener('error', function () {
          measure();
          tryStart();
        }, { once: true });
      });

      // Start immediately if enough layout exists, else after short fallback.
      if (measure() || imgs.every(function (img) { return img.complete; })) {
        tryStart();
      } else {
        setTimeout(tryStart, 400);
        setTimeout(tryStart, 1600);
      }

      onResize(function () {
        if (track.dataset.marqueeInit === 'true') measure();
      });
    });
  }

  /* ---------- Reviews carousel ---------- */
  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(function (carousel) {
      if (carousel.dataset.carouselInit === 'true') return;

      var track = carousel.querySelector('[data-carousel-track]');
      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      var gap = 32;
      var originals = Array.from(track.querySelectorAll('.review-card'));
      var total = originals.length;

      if (total < 2) {
        carousel.dataset.carouselInit = 'true';
        return;
      }

      carousel.classList.add('is-looping', 'has-overflow');

      originals.forEach(function (card) {
        var before = card.cloneNode(true);
        before.setAttribute('aria-hidden', 'true');
        before.dataset.reviewClone = 'before';
        track.insertBefore(before, track.firstChild);

        var after = card.cloneNode(true);
        after.setAttribute('aria-hidden', 'true');
        after.dataset.reviewClone = 'after';
        track.appendChild(after);
      });

      var index = total;
      var animating = false;
      var cachedStep = 364;

      function visibleCount() {
        var vc = window.matchMedia('(max-width: 749px)').matches ? 1 : 3;
        return Math.min(total, vc);
      }

      function layout() {
        var vc = visibleCount();
        var inner = carousel.clientWidth - 104;
        if (inner < 260) inner = carousel.clientWidth - 40;
        var cardW = vc === 1
          ? Math.min(inner, 340)
          : Math.max(220, Math.floor((inner - gap * (vc - 1)) / vc));

        track.querySelectorAll('.review-card').forEach(function (card) {
          card.style.flexBasis = cardW + 'px';
          card.style.width = cardW + 'px';
          card.style.maxWidth = cardW + 'px';
          card.style.minWidth = cardW + 'px';
        });

        cachedStep = cardW + gap;
        applyTransform(false);
      }

      function applyTransform(animate) {
        track.style.transition = animate
          ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          : 'none';
        track.style.transform = 'translate3d(' + (-index * cachedStep) + 'px,0,0)';
      }

      function go(direction) {
        if (animating) return;

        if (direction < 0 && index === total) {
          index = total * 2;
          applyTransform(false);
          void track.offsetHeight;
        }

        animating = true;
        index += direction;
        applyTransform(true);
      }

      track.addEventListener('transitionend', function (e) {
        if (e.target !== track || e.propertyName !== 'transform') return;
        var before = index;
        if (index >= total * 2) index -= total;
        else if (index < total) index += total;
        if (index !== before) applyTransform(false);
        animating = false;
      });

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });

      onResize(layout);
      layout();
      carousel.dataset.carouselInit = 'true';
    });
  }

  /* ---------- Accordion (click + keyboard; hover via CSS) ---------- */
  function initAccordions() {
    var triggers = document.querySelectorAll('[data-accordion-trigger]');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      if (trigger.dataset.accordionInit === 'true') return;
      trigger.dataset.accordionInit = 'true';
      trigger.addEventListener('click', function () {
        var item = trigger.closest('[data-accordion-item]');
        if (!item) return;
        item.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', item.classList.contains('is-open'));
      });
    });
  }

  /* ---------- Quiz ---------- */
  function initQuiz() {
    document.querySelectorAll('[data-quiz]').forEach(function (root) {
      var sid = root.id.replace('Quiz-', '');
      var configEl = document.querySelector('[data-quiz-config="' + sid + '"]');
      if (!configEl) return;

      var config;
      try { config = JSON.parse(configEl.textContent); } catch (e) { return; }
      var questions = config.questions || [];
      if (!questions.length) return;

      var intro = root.querySelector('[data-quiz-intro]');
      var stage = root.querySelector('[data-quiz-stage]');
      var emailStep = root.querySelector('[data-quiz-email]');
      var resultsStep = root.querySelector('[data-quiz-results]');
      var qContainer = root.querySelector('[data-quiz-question]');
      var progress = root.querySelector('[data-quiz-progress]');
      var current = root.querySelector('[data-quiz-current]');
      var totalEl = root.querySelector('[data-quiz-total]');
      var backBtn = root.querySelector('[data-quiz-back]');
      var startBtn = root.querySelector('[data-quiz-start]');

      var answers = new Array(questions.length).fill(null);
      var index = 0;
      if (totalEl) totalEl.textContent = questions.length;

      var storeKey = 'archetipas-quiz-' + sid;

      function show(el) { if (el) el.hidden = false; }
      function hide(el) { if (el) el.hidden = true; }

      function renderQuestion() {
        var q = questions[index];
        var html = '<div class="quiz-question reveal is-visible">';
        html += '<p class="quiz-question__num">' + String(index + 1).padStart(2, '0') + '</p>';
        html += '<p class="quiz-question__text">' + q.q + '</p>';
        html += '<div class="quiz-question__options">';
        q.o.forEach(function (opt, i) {
          var checked = answers[index] === i ? ' is-selected' : '';
          html += '<button type="button" class="quiz-option' + checked + '" data-opt="' + i + '">' +
                    '<span class="quiz-option__radio"></span>' +
                    '<span>' + opt[0] + '</span>' +
                  '</button>';
        });
        html += '</div></div>';
        qContainer.innerHTML = html;

        qContainer.querySelectorAll('[data-opt]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            answers[index] = parseInt(btn.dataset.opt, 10);
            qContainer.querySelectorAll('.quiz-option').forEach(function (b) { b.classList.remove('is-selected'); });
            btn.classList.add('is-selected');
            setTimeout(next, 260);
          });
        });

        if (current) current.textContent = index + 1;
        if (progress) progress.style.width = ((index) / questions.length * 100) + '%';
        if (backBtn) backBtn.hidden = index === 0;
      }

      function next() {
        if (answers[index] === null) return;
        if (index < questions.length - 1) {
          index++;
          renderQuestion();
          stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          if (progress) progress.style.width = '100%';
          hide(stage);
          show(emailStep);
          emailStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      function computeResult() {
        var total = answers.reduce(function (sum, a, i) {
          if (a === null) return sum;
          return sum + (questions[i].o[a][1] || 0);
        }, 0);
        if (total <= config.thresholdA) return 'a';
        if (total <= config.thresholdB) return 'b';
        return 'c';
      }

      function showResult(letter, skipScroll) {
        hide(intro);
        hide(stage);
        hide(emailStep);
        resultsStep.querySelectorAll('[data-quiz-result]').forEach(function (card) {
          card.hidden = card.getAttribute('data-quiz-result') !== letter;
        });
        show(resultsStep);
        if (!skipScroll) resultsStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      /* If the visitor already completed the quiz in this session, keep showing
         their result (so it never disappears, even after a page reload). */
      try {
        var saved = sessionStorage.getItem(storeKey);
        if (saved && resultsStep.querySelector('[data-quiz-result="' + saved + '"]')) {
          showResult(saved, true);
        }
      } catch (e) {}

      var emailForm = root.querySelector('[data-quiz-email-form]');
      var emailBtn = root.querySelector('[data-quiz-email-submit]');
      var emailInput = root.querySelector('[data-quiz-email-input]');

      function resetQuiz() {
        try { sessionStorage.removeItem(storeKey); } catch (e) {}
        answers = new Array(questions.length).fill(null);
        index = 0;
        if (emailInput) emailInput.value = '';
        hide(resultsStep);
        hide(stage);
        hide(emailStep);
        show(intro);
        intro.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (emailBtn && emailInput) {
        emailBtn.addEventListener('click', function () {
          if (!emailInput.checkValidity()) {
            emailInput.reportValidity();
            return;
          }
          var letter = computeResult();
          var tags = 'savigriovos-testas, rezultatas-' + letter.toUpperCase();
          try { sessionStorage.setItem(storeKey, letter); } catch (err) {}
          showResult(letter);
          subscribeEmail(emailInput.value, tags).catch(function () {});
        });
      }

      root.querySelectorAll('[data-quiz-restart]').forEach(function (btn) {
        btn.addEventListener('click', resetQuiz);
      });

      if (startBtn) {
        startBtn.addEventListener('click', function () {
          try { sessionStorage.removeItem(storeKey); } catch (e) {}
          answers = new Array(questions.length).fill(null);
          index = 0;
          if (emailInput) emailInput.value = '';
          hide(intro);
          hide(resultsStep);
          hide(emailStep);
          show(stage);
          renderQuestion();
          stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          if (index > 0) { index--; renderQuestion(); }
        });
      }
    });
  }

  function subscribeEmail(email, tags) {
    return fetch('/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'text/html'
      },
      body: new URLSearchParams({
        'form_type': 'customer',
        'utf8': '✓',
        'contact[email]': email,
        'contact[tags]': tags
      }).toString()
    });
  }

  function initNewsletter() {
    var storeKey = 'archetipas-subscribed';

    function getSubscribed() {
      try { return JSON.parse(localStorage.getItem(storeKey) || '[]'); } catch (e) { return []; }
    }

    function addSubscribed(email) {
      var list = getSubscribed();
      var normalized = email.toLowerCase();
      if (list.indexOf(normalized) === -1) {
        list.push(normalized);
        localStorage.setItem(storeKey, JSON.stringify(list));
      }
    }

    function isSubscribed(email) {
      return getSubscribed().indexOf(email.toLowerCase()) !== -1;
    }

    function showMessage(successEl, alreadyEl, type) {
      if (successEl) successEl.hidden = type !== 'success';
      if (alreadyEl) alreadyEl.hidden = type !== 'already';
    }

    document.querySelectorAll('[data-newsletter-form]').forEach(function (wrap) {
      var emailInput = wrap.querySelector('[data-newsletter-email]');
      var btn = wrap.querySelector('[data-newsletter-submit]');
      var success = wrap.parentElement.querySelector('[data-newsletter-success]') || wrap.querySelector('[data-newsletter-success]');
      var already = wrap.parentElement.querySelector('[data-newsletter-already]') || wrap.querySelector('[data-newsletter-already]');
      var tags = wrap.dataset.newsletterTags || 'newsletter';
      if (!emailInput || !btn) return;

      btn.addEventListener('click', function () {
        if (!emailInput.checkValidity()) {
          emailInput.reportValidity();
          return;
        }
        var email = emailInput.value.trim();
        btn.disabled = true;
        showMessage(success, already, null);

        if (isSubscribed(email)) {
          showMessage(success, already, 'already');
          btn.disabled = false;
          return;
        }

        subscribeEmail(email, tags)
          .then(function () {
            addSubscribed(email);
            showMessage(success, already, 'success');
            emailInput.value = '';
          })
          .catch(function () {
            addSubscribed(email);
            showMessage(success, already, 'success');
            emailInput.value = '';
          })
          .finally(function () { btn.disabled = false; });
      });
    });
  }

  function initPhoneInputs() {
    document.querySelectorAll('[data-phone-input]').forEach(function (input) {
      input.addEventListener('input', function () {
        input.value = input.value.replace(/[^\d+\s()-]/g, '');
      });
    });
  }

  function initCart() {
    function updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        if (count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    }

    /* Cart count comes from Liquid on first paint; refresh only after add */
    var forms = document.querySelectorAll('[data-cart-add]');
    if (!forms.length) return;

    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        var label = btn && btn.dataset.label ? btn.dataset.label : (btn ? btn.textContent : '');
        if (btn) btn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: parseInt(form.querySelector('[name="id"]').value, 10),
              quantity: parseInt(form.querySelector('[name="quantity"]').value, 10) || 1,
              properties: (function () {
                var props = {};
                form.querySelectorAll('[name^="properties["]').forEach(function (input) {
                  var match = input.name.match(/^properties\[(.+)\]$/);
                  if (match && input.value) props[match[1]] = input.value;
                });
                if (form.dataset.cartLabel && !props.Paketas) {
                  props.Paketas = form.dataset.cartLabel;
                }
                return props;
              })()
            }]
          })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart add failed');
            return r.json();
          })
          .then(function () { return fetch('/cart.js'); })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            if (form.hasAttribute('data-cart-redirect')) {
              window.location.href = '/cart';
              return;
            }
            if (btn) {
              btn.textContent = 'Pridėta ✓';
              setTimeout(function () {
                btn.disabled = false;
                btn.textContent = label;
              }, 1800);
            }
          })
          .catch(function () {
            if (btn) btn.disabled = false;
          });
      });
    });
  }

  function initCartDiscount() {
    var input = document.querySelector('[data-cart-discount]');
    var btn = document.querySelector('[data-cart-discount-apply]');
    if (!input || !btn) return;

    btn.addEventListener('click', function () {
      var code = input.value.trim();
      if (!code) {
        input.focus();
        return;
      }
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart');
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        btn.click();
      }
    });
  }

  function initMobileMenu() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var navWrap = document.querySelector('.header__nav-wrap');
    var backdrop = document.querySelector('[data-menu-backdrop]');
    var openIcon = document.querySelector('[data-menu-icon-open]');
    var closeIcon = document.querySelector('[data-menu-icon-close]');
    if (!toggle || !navWrap) return;

    function setMenuOpen(open) {
      navWrap.classList.toggle('is-open', open);
      if (backdrop) backdrop.hidden = !open;
      if (openIcon) openIcon.hidden = open;
      if (closeIcon) closeIcon.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('menu-open', open);
    }

    toggle.addEventListener('click', function () {
      setMenuOpen(!navWrap.classList.contains('is-open'));
    });

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        setMenuOpen(false);
      });
    }

    navWrap.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        setMenuOpen(false);
      });
    });

    onScroll(function () {
      if (navWrap.classList.contains('is-open')) setMenuOpen(false);
    });
  }

  /* Desktop header: fixed pin + hide on scroll down / reveal on scroll up */
  function initStickyHeader() {
    var header = document.querySelector('.header');
    if (!header) return;

    var bar =
      document.getElementById('shopify-section-group-header-group') ||
      header.closest('.shopify-section-group') ||
      header.closest('.shopify-section') ||
      header;

    var spacer = document.querySelector('[data-header-spacer]');
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.setAttribute('data-header-spacer', '');
      spacer.setAttribute('aria-hidden', 'true');
      bar.insertAdjacentElement('afterend', spacer);
    }

    var lastY = window.scrollY || 0;
    var delta = 8;
    var topReveal = 40;
    var mq = window.matchMedia('(min-width: 990px)');

    function syncSpacer() {
      if (!mq.matches) {
        bar.classList.remove('is-header-fixed', 'is-header-hidden', 'is-header-scrolled');
        spacer.style.display = 'none';
        spacer.style.height = '0px';
        return false;
      }
      bar.classList.add('is-header-fixed');
      spacer.style.display = 'block';
      spacer.style.height = bar.offsetHeight + 'px';
      return true;
    }

    function update() {
      if (!syncSpacer()) {
        lastY = window.scrollY || 0;
        return;
      }

      var y = window.scrollY || 0;
      var goingDown = y > lastY;
      var diff = Math.abs(y - lastY);

      bar.classList.toggle('is-header-scrolled', y > topReveal);

      if (y <= topReveal) {
        bar.classList.remove('is-header-hidden');
      } else if (diff > delta) {
        bar.classList.toggle('is-header-hidden', goingDown);
      }

      lastY = y;
    }

    onScroll(update);
    onResize(syncSpacer);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(update);
    }

    update();
  }

  function initProductVariants() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      var cards = form.querySelectorAll('.product__variant-card');
      var input = form.querySelector('[data-variant-input]');
      if (!cards.length || !input) return;

      var priceWrap = document.querySelector('[data-product-price]');
      var priceCurrent = priceWrap && priceWrap.querySelector('[data-price-current]');
      var priceCompare = priceWrap && priceWrap.querySelector('[data-price-compare]');
      var addBtn = form.querySelector('[data-add-to-cart]');
      var mainImage = document.querySelector('[id^="ProductMainImage-"]');

      function selectCard(card) {
        if (card.disabled) return;

        cards.forEach(function (c) {
          c.classList.toggle('is-selected', c === card);
          c.setAttribute('aria-checked', c === card ? 'true' : 'false');
        });

        input.value = card.getAttribute('data-variant-id');

        var available = card.getAttribute('data-variant-available') === 'true';
        var price = card.getAttribute('data-variant-price') || '';
        var compare = card.getAttribute('data-variant-compare') || '';
        var image = card.getAttribute('data-variant-image') || '';

        if (priceCurrent) priceCurrent.textContent = price;

        if (priceWrap) {
          if (compare) {
            if (!priceCompare) {
              priceCompare = document.createElement('s');
              priceCompare.className = 'text-muted';
              priceCompare.setAttribute('data-price-compare', '');
              priceWrap.insertBefore(priceCompare, priceCurrent);
            }
            priceCompare.textContent = compare;
            priceCompare.hidden = false;
          } else if (priceCompare) {
            priceCompare.hidden = true;
            priceCompare.textContent = '';
          }
        }

        if (addBtn) {
          addBtn.disabled = !available;
          addBtn.textContent = available
            ? (addBtn.getAttribute('data-label-add') || addBtn.textContent)
            : (addBtn.getAttribute('data-label-sold') || addBtn.textContent);
        }

        if (mainImage && image) {
          mainImage.src = image;
        }

        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', input.value);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }

      cards.forEach(function (card) {
        card.addEventListener('click', function () {
          selectCard(card);
        });
      });
    });
  }

  function init() {
    initReveal();
    initPartnersMarquee();
    initCarousels();
    initAccordions();
    initQuiz();
    initCart();
    initCartDiscount();
    initMobileMenu();
    initStickyHeader();
    initNewsletter();
    initPhoneInputs();
    initProductVariants();
    bindWindowListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
